/**
 * Payout Runs Panel
 * UI for running monthly payout cycles and viewing results
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  Autocomplete,
} from "@mui/material";
import { PlayArrow, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useAuth } from "../../../contexts/AuthContext.jsx";
import { runMonthlyPayout } from "../services/payoutService.js";
import { getPayeeOptions } from "../services/payeeOptionsService.js";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const PayoutRunsPanel = () => {
  const { hasPermission } = useAuth();
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canRunPayout = hasPermission("payouts:execute");

  // Load payee options on mount
  useEffect(() => {
    let mounted = true;
    const loadPayeeOptions = async () => {
      try {
        setLoadingOptions(true);
        const options = await getPayeeOptions();
        if (mounted) {
          setPayeeOptions(options);
          logInfo("Payee options loaded", { count: options.length });
        }
      } catch (err) {
        if (mounted) {
          const errorMessage =
            err.response?.data?.error || err.message || "Failed to load payees";
          setError(errorMessage);
          logError("Failed to load payee options", {
            error: errorMessage,
          });
        }
      } finally {
        if (mounted) setLoadingOptions(false);
      }
    };

    if (canRunPayout) {
      loadPayeeOptions();
    }

    return () => {
      mounted = false;
    };
  }, [canRunPayout]);

  const handleRunPayout = async () => {
    if (!selectedPayee?.id) {
      setError("Please select a payee");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await runMonthlyPayout({ payeeId: selectedPayee.id });
      // Handle response format (data may be nested or direct)
      const payload = response?.data || response;
      setResult(payload);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to run payout";
      setError(errorMessage);
      logError("Payout run failed", {
        payeeId: selectedPayee?.id,
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-ZA", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "R0.00";
    return `R${Number(amount).toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  if (!canRunPayout) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">
            You do not have permission to run payouts. Contact your administrator to
            request access.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Monthly Payout Runs
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Run Monthly Payout (20th Cycle)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a payout run for the current monthly cycle ending on the 20th.
            Cycle window is computed automatically based on global policy.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "flex-start" }}
          >
            <Autocomplete
              fullWidth
              options={payeeOptions}
              value={selectedPayee}
              onChange={(_, value) => setSelectedPayee(value)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={loadingOptions}
              disabled={loading || loadingOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Payee"
                  placeholder="Select a driver or partner"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingOptions ? (
                          <CircularProgress size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading && selectedPayee?.id) {
                  handleRunPayout();
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
              onClick={handleRunPayout}
              disabled={loading || !selectedPayee?.id}
              sx={{
                whiteSpace: "nowrap",
                minWidth: { xs: "100%", sm: 180 },
              }}
            >
              {loading ? "Running..." : "Run Payout"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircle color="success" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Payout Run {result.created === false ? "Skipped" : "Completed"}
              </Typography>
            </Box>

            {result.run && (
              <>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payout Run ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                      {result.run.id}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cycle Window
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(result.run.cycleStart)} -{" "}
                      {formatDate(result.run.cycleEnd)}
                    </Typography>
                  </Box>

                  {result.run.totals && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Totals
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Chip
                          label={`Gross: ${formatCurrency(result.run.totals.gross)}`}
                          color="default"
                        />
                        <Chip
                          label={`Platform Fee: ${formatCurrency(
                            result.run.totals.platformFee
                          )}`}
                          color="info"
                        />
                        <Chip
                          label={`Net: ${formatCurrency(result.run.totals.net)}`}
                          color="success"
                        />
                      </Stack>
                    </Box>
                  )}

                  {result.run.includedJobs && result.run.includedJobs.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Included Jobs ({result.run.includedJobs.length})
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {result.run.includedJobs.map((jobId) => (
                          <Chip
                            key={jobId}
                            label={jobId}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {result.run.excludedJobs &&
                    result.run.excludedJobs.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Excluded Jobs ({result.run.excludedJobs.length})
                        </Typography>
                        <Stack spacing={1}>
                          {result.run.excludedJobs.map((excluded, idx) => (
                            <Box key={idx}>
                              <Chip
                                label={excluded.jobId}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {excluded.reasons?.join(", ") || "No reason provided"}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                </Stack>
              </>
            )}

            {result.message && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {result.message}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PayoutRunsPanel;
